/**
 * Status callbacks: runner → webhooks Worker.
 * Three shapes, one per terminal transition + 'running'.
 */
import { z } from 'zod';
import { SummaryMetricsSchema } from './lighthouse.js';

const Base = z.object({
  snapshot_uuid: z.string().startsWith('ss-'),
  request_id: z.string().optional(),
  timestamp: z.string().datetime(),
});

export const RunningCallbackSchema = Base.extend({
  status: z.literal('running'),
});

/**
 * Per-entity third-party execution, carried on the success callback and
 * fanned out to the `snapshots_third_parties` Tinybird datasource by
 * webhooks. Populated from the runner's `buildThirdParties(lhr)`
 * (Lighthouse `third-party-summary` / `third-parties-insight`).
 *
 * `main_thread_ms` is the durable ranking signal — `null` only on the rare
 * pre-LH-13-migration artifact that predates per-entity main-thread time.
 * `blocking_ms` is `null` on every LH ≥13 run (the `third-parties-insight`
 * successor dropped it); carried for back-compat with LH ≤12 rows.
 */
export const ThirdPartyCallbackItemSchema = z.object({
  entity: z.string(),
  main_thread_ms: z.number().nullable(),
  blocking_ms: z.number().nullable(),
  transfer_kb: z.number(),
  request_count: z.number().int().nonnegative(),
  /**
   * Mean network response latency (ms) across this entity's requests —
   * the runner's per-entity aggregate of the network log's TTFB. The
   * "connection time" signal (how slow the third party is to respond),
   * distinct from main-thread execution. Null when the network-requests
   * audit didn't run or the entity had no attributable requests.
   *
   * Optional (not just nullable) so callbacks from a runner that predates
   * this field — including audits in flight across the rollout — still
   * validate; webhooks coerces a missing value to null.
   */
  ttfb_ms: z.number().nullable().optional(),
});
export type ThirdPartyCallbackItem = z.infer<typeof ThirdPartyCallbackItemSchema>;

export const SuccessCallbackSchema = Base.extend({
  status: z.literal('success'),
  metrics: SummaryMetricsSchema,
  /** R2 object key for the full Lighthouse JSON (gzipped, summary form). */
  report_r2_key: z.string(),
  /**
   * R2 object key for the full audit artifact (raw LHR + traces +
   * projections) in the artifacts bucket. Added in 0.9.5; optional so
   * older runners keep working until the full-capture pipeline rolls out.
   */
  artifact_r2_key: z.string().optional(),
  /** Runtime identifier — `gcp-cloud-run` for the current runner. */
  runtime: z.string().optional(),
  /** Chromium version string captured at audit time (e.g. `Chrome/126.0.0.0`). */
  chromium_version: z.string().optional(),
  /**
   * Lighthouse version that produced this run, read directly from
   * `lhr.lighthouseVersion` by the runner (e.g. `13.3.0`). Lets
   * downstream readers attribute score drift across version bumps
   * without inferring from the audit date.
   */
  lighthouse_version: z.string().optional(),
  duration_ms: z.number().int().nonnegative(),
  /**
   * Per-entity third-party execution breakdown. Optional so runners that
   * predate this field keep validating; absent ⇒ webhooks ingests no
   * `snapshots_third_parties` rows for the audit. Computed unconditionally
   * by the runner (not gated on artifact storage), so it's present on
   * every current success callback that detected third parties.
   */
  third_parties: z.array(ThirdPartyCallbackItemSchema).optional(),
});

export const ErrorCallbackSchema = Base.extend({
  status: z.literal('error'),
  error_code: z.string(),
  error_message: z.string(),
  /** True => no more retries wanted (e.g. bad URL). False => transient, let task-queue retry. */
  permanent: z.boolean(),
  duration_ms: z.number().int().nonnegative().optional(),
  /**
   * R2 object key for the gzipped Lighthouse JSON. Populated on the
   * `lhr.runtimeError` branch — the runner stored the LHR before
   * classifying the audit as errored, so the diagnostic data is
   * already in R2. Lets the dashboard's runtime-error UI show the
   * caller the actual LH error context (NOT_HTML, NO_FCP, network
   * failure details). Absent for crashes that fail before the LHR
   * exists (`runAudit` threw); webhooks treats the field as nullable.
   */
  report_r2_key: z.string().optional(),
  /** R2 object key for the full audit artifact, when artifact storage is enabled. */
  artifact_r2_key: z.string().optional(),
});

export const CallbackSchema = z.discriminatedUnion('status', [
  RunningCallbackSchema,
  SuccessCallbackSchema,
  ErrorCallbackSchema,
]);

export type RunningCallback = z.infer<typeof RunningCallbackSchema>;
export type SuccessCallback = z.infer<typeof SuccessCallbackSchema>;
export type ErrorCallback = z.infer<typeof ErrorCallbackSchema>;
export type Callback = z.infer<typeof CallbackSchema>;
